/**
 * Date utility functions for filter condition row
 * Handles conversion between local datetime and UTC ISO format
 */

/**
 * Convert local datetime to UTC ISO format
 * @param localDateTime - Local datetime string (YYYY-MM-DDTHH:mm)
 * @returns UTC ISO string (YYYY-MM-DDTHH:mm:ss.sssZ)
 */
export const localDateTimeToUTC = (localDateTime: string): string => {
  if (!localDateTime) return ''
  // Parse the local datetime string and convert to UTC ISO format with milliseconds
  const date = new Date(localDateTime)
  // toISOString() always returns format: 2026-04-01T15:21:00.000Z
  return date.toISOString()
}

/**
 * Normalize any datetime string to proper UTC ISO format with milliseconds
 * @param dateString - Any datetime string
 * @returns UTC ISO string (YYYY-MM-DDTHH:mm:ss.sssZ) or original string if invalid
 */
export const normalizeToUTC = (dateString: string): string => {
  if (!dateString) return ''
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return dateString
    // Always return format: 2026-04-01T15:21:00.000Z
    return date.toISOString()
  } catch {
    return dateString
  }
}

/**
 * Convert UTC ISO format to local datetime for input display
 * @param utcString - UTC ISO string (YYYY-MM-DDTHH:mm:ss.sssZ)
 * @returns Local datetime string (YYYY-MM-DDTHH:mm) for datetime-local input
 */
export const utcToLocalDateTime = (utcString: string): string => {
  if (!utcString) return ''
  try {
    // First normalize the UTC string to ensure it's in proper format
    const normalizedUTC = normalizeToUTC(utcString)
    const date = new Date(normalizedUTC)
    if (isNaN(date.getTime())) return utcString
    // Format as YYYY-MM-DDTHH:mm for datetime-local input
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}`
  } catch {
    return utcString
  }
}
